import { Grid, Stack, TextField, RadioGroup, FormControlLabel, Radio } from '@mui/material'
import { useEffect, useState, useCallback } from 'react'
import { renderToString } from 'react-dom/server'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'
import Button from '../../../../shared/UI/Button'
import Paragraph from '../../../../shared/UI/Paragraph'
import { getLocalDateString } from '../../../../utils/date-utils'
import { manualRequestsService } from '../../../../services/myActionsService'
import { RootStore, useAppDispatch } from '../../../../redux/store'
import { showErrorMessage, showSuccessMessage } from '../../../../redux/reducers/snackbarReducer'
import { ScaMaintenanceRequest } from '../../../../types/absence-sca'
import UserErrorMessage from '../../../../utils/errorFilter'
import { AbsenceOccurrence } from '../../../../types/absence'
import { BaseResponse } from '../../../../types/base-response'
import NoDataFound from '../../../../shared/UI/NoDataFound'
import LoadingIndicator from '../../../../shared/UI/LoadingIndicator'
import { EmployeeDetailsResponse } from '../../../../types/employee'
import EDPrint from '../../EDPrint'
import AutocompleteList from '../../../../shared/UI/AutocompleteList'
import { sicknessTypeSortedData } from '../../../../utils/app-utils'
import { SelectOption } from '../../../../services/dashboardService'
import { AbsenceStatus } from '../../../../utils/constants'
import { DateFormats } from '../../../../api/absence/tempTypes/generalprops'

type Props = {
  absenceOccurrence: AbsenceOccurrence
  onSubmit: () => void
  onCancel?: () => void
  setIsEdComplete?: (res: boolean) => void
}

interface EdResponseType {
  nonWorkingDays?: number
  normalWeekWorkingDays?: number
  absenceReasonText?: string
  medicalPractitionerConsultedType: string
  medicalPractitionerComments?: string
  displayName: string
  date: string
  ip: string
}

interface editableStateType {
  nonWorkingDays?: string | number
  normalWeekWorkingDays?: string | number
  absenceReasonText?: string
  absenceTypeId?: number
  medicalPractitionerConsultedType: string
  medicalPractitionerComments?: string
}

const editableStateDefaults = {
  nonWorkingDays: '',
  normalWeekWorkingDays: '',
  absenceReasonText: '',
  medicalPractitionerConsultedType: 'No',
  medicalPractitionerComments: '',
}
const REGEX = /^[0-9\b]+$/

function AddEDUK({ absenceOccurrence, onSubmit, onCancel, setIsEdComplete }: Props) {
  const [edSubmission, setEdSubmission] = useState<ScaMaintenanceRequest>()
  const [nonWorkingDaysError, setNonWorkingDaysError] = useState<boolean>(false)
  const [normalWeekWorkingDaysError, setNormalWeekWorkingDaysError] = useState<boolean>(false)
  const [absenceReasonTextError, setAbsenceReasonTextError] = useState<boolean>(false)
  const [medicalPractitionerCommentsError, setMedicalPractitionerCommentsError] =
    useState<boolean>(false)
  const [hasChanges, setHasChanges] = useState<boolean>(false)
  const [edResponse, setEdResponse] = useState<EdResponseType>()
  const [initialEditableState, setInitialEditableState] = useState<editableStateType>()
  const [changeableFields, setChangeableFields] = useState<editableStateType>(editableStateDefaults)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false)
  const [sicknessTypes, setSicknessTypes] = useState<SelectOption[]>([])

  const employeeDetails = useSelector<RootStore, EmployeeDetailsResponse>(
    (state: RootStore) => state.appSettings.employeeDetails
  )

  const dispatch = useAppDispatch()

  const totalHoursLost = () => {
    let result = 0
    absenceOccurrence.absenceDays.forEach(day => {
      result += day.hoursLost
    })
    return result
  }

  const awaitingEd =
    absenceOccurrence.scaRequired &&
    !absenceOccurrence.hasValidSca &&
    (absenceOccurrence.absenceStatus.name === AbsenceStatus.AWAITING_ED ||
      absenceOccurrence.absenceStatus.name === AbsenceStatus.AWAITING_SCA)

  const edButtonLabel = awaitingEd ? 'Save and Sign' : 'Save and Re-submit'

  const consultedMedicalProfessional = (type: string) => {
    switch (type) {
      case 'MedicalPractitionerConsulted':
        return 'Yes'
      case 'MedicalPractitionerNotConsulted':
        return 'No'
      default:
        return 'Na'
    }
  }

  useEffect(() => {
    if (!awaitingEd) {
      setIsLoading(true)
      manualRequestsService
        .getSCA(absenceOccurrence.id)
        .then(e => {
          setChangeableFields({
            nonWorkingDays: Number(e.nonWorkingDays),
            normalWeekWorkingDays: e.normalWeekWorkingDays,
            absenceReasonText: e.absenceReasonText,
            medicalPractitionerConsultedType: consultedMedicalProfessional(
              e.medicalPractitionerConsultType
            ),
            medicalPractitionerComments: e.medicalPractitionerComments,
          })
          setInitialEditableState({
            nonWorkingDays: Number(e.nonWorkingDays),
            normalWeekWorkingDays: e.normalWeekWorkingDays,
            absenceReasonText: e.absenceReasonText,
            medicalPractitionerConsultedType: consultedMedicalProfessional(
              e.medicalPractitionerConsultType
            ),
            medicalPractitionerComments: e.medicalPractitionerComments,
          })
          setEdResponse({
            nonWorkingDays: e.nonWorkingDays,
            normalWeekWorkingDays: e.normalWeekWorkingDays,
            absenceReasonText: e.absenceReasonText,
            medicalPractitionerConsultedType: consultedMedicalProfessional(
              e.medicalPractitionerConsultType
            ),
            medicalPractitionerComments: e.medicalPractitionerComments,
            displayName: e.signedByEmployeeName,
            date: new Date(e.signedDateTime).toLocaleString(navigator.language),
            ip: e.signedOnMachine,
          })
          setIsLoading(false)
        })
        .catch(err => {
          const response: BaseResponse = err.response.data
          response.errors.forEach(error => {
            dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
          })
        })
    }

    if (absenceOccurrence) {
      setChangeableFields({
        ...changeableFields,
        absenceTypeId: absenceOccurrence.absenceDays[0].absenceType.absenceTypeId,
      })
    }

    sicknessTypeSortedData(employeeDetails.employeeId).then(res => setSicknessTypes(res))
  }, [])

  const edDetailHasChanged = useCallback((): boolean => {
    if (changeableFields && initialEditableState) {
      return Object.keys(changeableFields).some(
        key =>
          changeableFields[key as keyof editableStateType] !==
          initialEditableState[key as keyof editableStateType]
      )
    }

    return false
  }, [initialEditableState, changeableFields])

  useEffect(() => {
    setHasChanges(edDetailHasChanged())
  }, [edDetailHasChanged])

  const isManager = absenceOccurrence.employeeResponse.id !== employeeDetails.employeeId
  const isDisabledReSubmitButton = (!awaitingEd && !hasChanges) || isManager

  const handleSubmit = () => {
    if (!changeableFields) {
      return
    }
    const normalWeekWorkingDaysHasError =
      changeableFields.normalWeekWorkingDays === undefined ||
      Number(changeableFields.normalWeekWorkingDays) < 1 ||
      Number(changeableFields.normalWeekWorkingDays) > 7 ||
      !changeableFields.normalWeekWorkingDays

    if (changeableFields.medicalPractitionerConsultedType.toLowerCase() !== 'yes') {
      setMedicalPractitionerCommentsError(false)
    }

    if (
      normalWeekWorkingDaysHasError ||
      !changeableFields.absenceReasonText ||
      (!changeableFields.medicalPractitionerComments &&
        changeableFields.medicalPractitionerConsultedType.toLowerCase() === 'yes')
    ) {
      if (changeableFields.nonWorkingDays === undefined) {
        setNonWorkingDaysError(true)
      }
      if (normalWeekWorkingDaysHasError) {
        setNormalWeekWorkingDaysError(true)
      }
      if (!changeableFields.absenceReasonText) {
        setAbsenceReasonTextError(true)
      }
      if (
        !changeableFields.medicalPractitionerComments &&
        changeableFields.medicalPractitionerConsultedType.toLowerCase() === 'yes'
      ) {
        setMedicalPractitionerCommentsError(true)
      }
      return
    }

    setEdSubmission({
      ...edSubmission,
      absenceId: absenceOccurrence.id,
      absenceTypeId:
        Number(changeableFields.absenceTypeId) ||
        Number(absenceOccurrence.absenceDays[0].absenceType.absenceTypeId),
      employee: {
        employeeId: absenceOccurrence.employeeResponse.id,
        displayName: absenceOccurrence.employeeResponse.displayName,
      },
      department: {
        departmentId: absenceOccurrence.departmentId,
        departmentName: String(absenceOccurrence.departmentName),
      },
      dateFrom: absenceOccurrence.startDate,
      dateTo: absenceOccurrence.endDate,
      totalDaysOfAbsence: absenceOccurrence.absenceDays.length,
      daysAbsent: absenceOccurrence.absenceDays.length,
      nonWorkingDays: Number(changeableFields.nonWorkingDays)
        ? Number(changeableFields.nonWorkingDays)
        : 0,
      normalWeekWorkingDays: Number(changeableFields.normalWeekWorkingDays)
        ? Number(changeableFields.normalWeekWorkingDays)
        : 0,
      hoursOfAbsence: totalHoursLost(),
      absenceType: {
        absenceTypeId:
          Number(changeableFields.absenceTypeId) ||
          Number(absenceOccurrence.absenceDays[0].absenceType.absenceTypeId),
        reason: String(changeableFields.absenceReasonText),
        reasonType: String(absenceOccurrence.absenceDays[0].absenceType.reasonType),
      },
      absenceReasonText: changeableFields.absenceReasonText,
      medicalPractitionerConsultedType: changeableFields.medicalPractitionerConsultedType,
      medicalPractitionerComments: changeableFields.medicalPractitionerComments
        ? changeableFields.medicalPractitionerComments
        : '',
      signedDateTime: format(new Date(), DateFormats.DATE_AND_TIME),
    })
    setIsEdComplete?.(true)
  }

  useEffect(() => {
    if (!edSubmission) {
      return
    }

    const updateED = () => {
      setIsSubmitLoading(true)
      manualRequestsService
        .resubmitSCA(edSubmission)
        .then(res => {
          setIsSubmitLoading(false)
          dispatch(showSuccessMessage('ED update submitted'))
          onSubmit()
        })
        .catch(err => {
          setIsSubmitLoading(false)
          const response: BaseResponse = err.response.data
          response.errors.forEach(error => {
            dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
          })
        })
    }

    const createED = () => {
      setIsSubmitLoading(true)
      manualRequestsService
        .createSCA(edSubmission)
        .then(res => {
          setIsSubmitLoading(false)
          dispatch(showSuccessMessage('ED submitted'))
          onSubmit()
        })
        .catch(err => {
          setIsSubmitLoading(false)
          const response: BaseResponse = err.response.data
          response.errors.forEach(error => {
            dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
          })
        })
    }

    if (awaitingEd) {
      createED()
    } else {
      updateED()
    }
  }, [dispatch, edSubmission])

  const handlePrint = () => {
    const content = renderToString(
      <EDPrint absenceOccurrence={absenceOccurrence!} ed={edResponse!} />
    )
    const winPrint: any = window.open(
      '',
      '',
      'left=0,top=0,width=384,height=900,toolbar=0,scrollbars=0,status=0'
    )
    winPrint.document.write('<html><head>')
    winPrint.document.write(
      `<title>Employee Declaration ${absenceOccurrence.employeeResponse.displayName}</title>`
    )
    winPrint.document.write('<link rel="stylesheet" href="../../../print.css">')
    winPrint.document.write('</head><body>')
    winPrint.document.write(content)
    winPrint.document.write('</body></html>')
    setTimeout(() => {
      winPrint.print()
    }, 500)
    setTimeout(() => {
      winPrint.close()
    }, 700)
  }

  return (
    <Grid container xl={12} rowSpacing={4} columnSpacing={4} pb={4}>
      {absenceOccurrence ? (
        <>
          <Grid item sm={12}>
            <Paragraph>
              This form should be completed upon your return to work following any period of absence
              of up to 7 days, and digitally signed using the provided controls. If you are
              returning to work after a period of more than 7 days absence you must also provide a
              supporting Medical Certificate to your Departmental Manager.
            </Paragraph>
          </Grid>
          {/* Left Column */}
          <Grid item xs={12} lg={6} display="flex" pt={1}>
            <Grid container xl={12} alignContent="flex-start">
              {/* Name */}
              <Grid item xs={12} mb={1}>
                <Paragraph weight="bold">Name</Paragraph>
              </Grid>
              <Grid item xs={12}>
                <Paragraph>
                  {absenceOccurrence && absenceOccurrence.employeeResponse.displayName}
                </Paragraph>
              </Grid>
              {/* Date From */}
              <Grid item xs={12} display="flex" mt={4} mb={1}>
                <Paragraph weight="bold">Date From</Paragraph>
              </Grid>
              <Grid item xs={12}>
                {absenceOccurrence && (
                  <Paragraph>
                    {getLocalDateString(absenceOccurrence && absenceOccurrence.startDate)}
                  </Paragraph>
                )}
              </Grid>
              {/* Date To */}
              <Grid item xs={12} display="flex" mt={4} mb={1}>
                <Paragraph weight="bold">Date To</Paragraph>
              </Grid>
              <Grid item xs={12}>
                {absenceOccurrence && (
                  <Paragraph>
                    {getLocalDateString(absenceOccurrence && absenceOccurrence.endDate)}
                  </Paragraph>
                )}
              </Grid>
              {/* Total Hours Lost */}
              <Grid item xs={12} mt={4} mb={1}>
                <Paragraph weight="bold">Total number of hours absent</Paragraph>
              </Grid>
              <Grid item xs={12}>
                <Paragraph>{absenceOccurrence && totalHoursLost()}</Paragraph>
              </Grid>
              {/* Absence Reason */}
              <Grid item xs={12} mt={4} mb={1}>
                <Paragraph weight="bold">Absence Type</Paragraph>
              </Grid>
              <Grid item xs={12}>
                <Paragraph>
                  {absenceOccurrence && absenceOccurrence.absenceDays[0].absenceType.reasonType}
                </Paragraph>
              </Grid>
            </Grid>
          </Grid>
          {/* Right Column */}
          <Grid item xs={12} lg={6} pt={1}>
            <Grid container xl={12} alignContent="flex-start">
              <Grid item xs={12} mb={1}>
                <Paragraph weight="bold">Total Number of days absent</Paragraph>
              </Grid>
              <Grid item xs={12}>
                <Paragraph>{absenceOccurrence && absenceOccurrence.absenceDays.length}</Paragraph>
              </Grid>
              {/* Total Number of days absent */}
              <Grid item xs={12} mt={4} mb={1}>
                <Paragraph weight="bold">Of which resulted in absence from work</Paragraph>
              </Grid>
              <Grid item xs={12}>
                <Paragraph>{absenceOccurrence && absenceOccurrence.absenceDays.length}</Paragraph>
              </Grid>
              {/* of which were during non-working days off */}
              <Grid item xs={12} mt={5}>
                <TextField
                  id="non-working-days"
                  label="of which were during non-working days off"
                  variant="outlined"
                  value={changeableFields?.nonWorkingDays}
                  fullWidth
                  onChange={e => {
                    setNonWorkingDaysError(false)
                    setChangeableFields({
                      ...changeableFields,
                      nonWorkingDays:
                        e.target.value !== null && REGEX.test(e.target.value) ? e.target.value : '',
                    })
                    setHasChanges(true)
                  }}
                  placeholder="0"
                  disabled={isManager}
                  error={nonWorkingDaysError}
                  InputLabelProps={{ shrink: true }}
                  onWheel={event => event.target instanceof HTMLElement && event.target.blur()}
                />
              </Grid>
              {/* Usual number of days worked per week */}
              <Grid item xs={12} mt={5}>
                <TextField
                  id="working-days"
                  label="Usual number of days worked per week must be a value between 1 - 7"
                  variant="outlined"
                  value={changeableFields?.normalWeekWorkingDays}
                  fullWidth
                  onChange={e => {
                    setNormalWeekWorkingDaysError(false)
                    setChangeableFields({
                      ...changeableFields,
                      normalWeekWorkingDays:
                        e.target.value !== null && REGEX.test(e.target.value) ? e.target.value : '',
                    })
                    setHasChanges(true)
                  }}
                  helperText="Required"
                  placeholder="0"
                  disabled={isManager}
                  error={normalWeekWorkingDaysError}
                  InputLabelProps={{ shrink: true }}
                  onWheel={event => event.target instanceof HTMLElement && event.target.blur()}
                />
              </Grid>
            </Grid>
          </Grid>
          {/* Middle Column */}
          <Grid item xs={12}>
            {/* Reason for absence */}
            <Grid item xs={12}>
              <Paragraph weight="bold" padding="0 0 14px 0">
                Reason for absence
              </Paragraph>
              <AutocompleteList
                id="absenceReason"
                label="Please disclose the reason for your sickness absence using the closest reason from the dropdown"
                data={sicknessTypes}
                textField="displayValue"
                value={{ displayValue: changeableFields.absenceReasonText }}
                onChange={e => {
                  setAbsenceReasonTextError(false)
                  setChangeableFields({
                    ...changeableFields,
                    absenceReasonText: e.target.innerText.toString(),
                    absenceTypeId: sicknessTypes.find(
                      x => x.displayValue === e.target.innerText.toString()
                    )?.value,
                  })
                  setHasChanges(true)
                }}
                error={absenceReasonTextError}
                disabled={isManager}
                disableClearable
                helperText="Required"
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {/* Has Medical Advice been sought? */}
            <Grid container>
              <Grid item xs={12}>
                <Paragraph weight="bold" padding="10px 0 0 0">
                  Has Medical Advice been sought?
                </Paragraph>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-start">
                <RadioGroup
                  row
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="controlled-radio-buttons-group"
                  value={changeableFields?.medicalPractitionerConsultedType}
                  onChange={e => {
                    setChangeableFields({
                      ...changeableFields,
                      medicalPractitionerConsultedType: e.target.value,
                    })
                    setHasChanges(true)
                    if (
                      e.target.value.toLowerCase() === 'yes' &&
                      !changeableFields?.medicalPractitionerComments
                    ) {
                      setMedicalPractitionerCommentsError(true)
                    } else {
                      setMedicalPractitionerCommentsError(false)
                    }
                  }}
                >
                  <FormControlLabel
                    value="Yes"
                    disabled={isManager}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="No"
                    disabled={isManager}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </Grid>
              <Grid item xs={6} mt="33px">
                <TextField
                  id="practitioner-details"
                  label="Details:"
                  variant="outlined"
                  value={changeableFields?.medicalPractitionerComments}
                  rows={3}
                  multiline
                  fullWidth
                  disabled={isManager}
                  onChange={e => {
                    setMedicalPractitionerCommentsError(false)
                    setChangeableFields({
                      ...changeableFields,
                      medicalPractitionerComments: e.target.value,
                    })
                    setHasChanges(true)
                  }}
                  error={medicalPractitionerCommentsError}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} mt="33px">
              <Paragraph weight="medium">
                I certify that I was not fit for work on the dates stated above. I hereby confirm
                that the details that I have provided are true and accurate. I acknowledge that
                providing false information may result in disciplinary action.
              </Paragraph>
            </Grid>
          </Grid>
          <Grid item sm={12}>
            <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
              {edButtonLabel !== 'Save and Sign' && (
                <>
                  {edResponse && (
                    <Paragraph weight="medium">
                      {`Digitally signed by ${edResponse.displayName} on ${edResponse.date} on machine ${edResponse.ip}`}
                    </Paragraph>
                  )}
                  <Button
                    label="Print"
                    color="secondary"
                    variant="outlined"
                    onClick={handlePrint}
                  />
                </>
              )}
              <Button label="Cancel" color="secondary" variant="outlined" onClick={onCancel} />
              {!isManager && (
                <Button
                  label={edButtonLabel}
                  color="primary"
                  variant="outlined"
                  onClick={handleSubmit}
                  disabled={isDisabledReSubmitButton}
                  loading={isSubmitLoading}
                />
              )}
            </Stack>
          </Grid>
        </>
      ) : (
        <>
          <NoDataFound show={!isLoading && !absenceOccurrence} />
          <LoadingIndicator show={isLoading} />
        </>
      )}
    </Grid>
  )
}

export default AddEDUK
